import { useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { PAGES } from 'constants/pages';
import { formatDateAsAnniversary, getNextMondayDate, getWeekNumber } from 'helpers/dateTimes';
import { ProductArrangementsToUpdate, ProductReplacementPopupInfo } from './types';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { Loader } from 'components/Loader';
import { TotemDatePicker } from 'components/TotemDatePicker';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { ProductReplacementPopup } from './ProductReplacementPopup';
import { SiteMenus } from './SiteMenus';
import { TotemInput } from 'components/TotemInput';
import { MenuProductFragmentFragment, useGetMenuProductArrangementsQuery } from 'data/__generated__';
import { UpdateMenuProductArrangementsButton } from './UpdateMenuProductArrangementsButton';
import { MenuProductSummary } from 'pages/MicrostoreMenus/MicrostoreMenuProductArrangements/MenuProductSummary';

type ParamTypes = {
    siteId: string;
    selectedDate: string;
};

export const MicrostoreMenuProductArrangements = () => {
    const navigate = useNavigate();

    const { selectedDate: selectedDateFromUrl } = useParams<ParamTypes>();
    const [productArrangementsToUpdate, setProductArrangementsToUpdate] = useState<ProductArrangementsToUpdate>({});
    const [productReplacementPopupInfo, setProductReplacementPopupInfo] = useState<ProductReplacementPopupInfo | null>(
        null,
    );

    const selectedDate = selectedDateFromUrl ? new Date(selectedDateFromUrl) : getNextMondayDate();

    function resetProductArrangementsToUpdate() {
        setProductArrangementsToUpdate({});
    }

    function updateProductArrangementsToUpdate({
        productArrangementId,
        productArrangementIndex,
        rotationNumber,
        currentProduct,
        newProduct,
        isInProductArrangement,
    }: {
        productArrangementId: string;
        productArrangementIndex: number;
        rotationNumber: number;
        currentProduct: MenuProductFragmentFragment['product'] | null;
        newProduct: MenuProductFragmentFragment['product'] | null;
        isInProductArrangement: boolean;
    }) {
        setProductArrangementsToUpdate((prev) => ({
            ...prev,
            [productArrangementId]: {
                ...prev?.[productArrangementId],
                [productArrangementIndex]: {
                    ...prev?.[productArrangementId]?.[productArrangementIndex],
                    [rotationNumber]: {
                        currentProduct,
                        newProduct,
                        isInProductArrangement,
                    },
                },
            },
        }));
    }

    const setSelectedDate = useCallback(
        (selectedDate: Date) => {
            navigate(
                `/menu-product-arrangements/${formatDateAsAnniversary({
                    dateTime: selectedDate,
                    useNewFormat: true,
                })}`,
            );
        },
        [selectedDate],
    );

    const {
        data: menuProductArrangementsData,
        loading: menuProductArrangementsLoading,
        error: menuProductArrangementsError,
    } = useGetMenuProductArrangementsQuery({
        variables: {
            date: formatDateAsAnniversary({ dateTime: selectedDate, useNewFormat: true }),
        },
        fetchPolicy: 'network-only',
    });

    if (menuProductArrangementsLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (menuProductArrangementsError) {
        toast.error(`Erreur lors de la récupération des menus: ${menuProductArrangementsError}`);
    }

    if (!menuProductArrangementsData) {
        toast.error('Erreur lors de la récupération des menus');
    }

    const menuProductArrangements = menuProductArrangementsData?.menuProductArrangements || [];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.menusDetails} />
                </HeaderTitle>
                <Filters>
                    <Link to={PAGES.menus.url}>
                        <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                    </Link>
                    <UpdateMenuProductArrangementsButton
                        date={selectedDate}
                        productArrangementsToUpdate={productArrangementsToUpdate}
                        resetProductArrangementsToUpdate={resetProductArrangementsToUpdate}
                    />
                    <TotemInput
                        className="w-[120px]"
                        value={'Semaine ' + getWeekNumber(selectedDate)}
                        onChange={() => {}}
                        disabled
                    />
                    <TotemDatePicker
                        selected={selectedDate}
                        onChange={(date) => {
                            setSelectedDate(date || getNextMondayDate());
                            resetProductArrangementsToUpdate();
                        }}
                    />
                </Filters>
            </Header>
            <ProductReplacementPopup
                isOpen={!!productReplacementPopupInfo}
                productReplacementPopupInfo={productReplacementPopupInfo}
                setIsOpen={() => setProductReplacementPopupInfo(null)}
                updateProductArrangementsToUpdate={updateProductArrangementsToUpdate}
            />
            {menuProductArrangements.length > 0 ? (
                <Content>
                    <MenuProductSummary
                        menuProductArrangements={menuProductArrangements}
                        productArrangementsToUpdate={productArrangementsToUpdate}
                    />
                    <SiteMenus
                        menuProductArrangements={menuProductArrangements}
                        productArrangementsToUpdate={productArrangementsToUpdate}
                        setProductReplacementPopupInfo={setProductReplacementPopupInfo}
                        updateProductArrangementsToUpdate={updateProductArrangementsToUpdate}
                    />
                </Content>
            ) : (
                <NoMenuLabel>
                    <h1>Aucun menu trouvé à cette date</h1>
                </NoMenuLabel>
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const Filters = styled.div`
    display: flex;
    align-items: center;

    & > :not(:first-child) {
        margin-left: 20px;
    }
`;

const Content = styled.div`
    flex: 1;
    overflow-x: auto;
    overflow-y: visible;
    max-width: 100vw;
`;

const NoMenuLabel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
`;
